import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faDownload, 
  faUpload, 
  faCode, 
  faFileExport,
  faSpinner,
  faCheckCircle,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

const AdvancedTab = ({ agentId, agentOverview }) => {
  const { token } = useAuth();
  const [isExporting, setIsExporting] = useState(false);
  const [exportData, setExportData] = useState(null);
  const [showRawConfig, setShowRawConfig] = useState(false);

  const handleExportConfiguration = async () => {
    try {
      setIsExporting(true);
      const response = await fetch(`/api/agents/${agentId}/settings/export`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setExportData(data.agent_configuration);
        
        // Also trigger download
        const blob = new Blob([JSON.stringify(data.agent_configuration, null, 2)], {
          type: 'application/json'
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${agentOverview?.agent_name || 'agent'}_configuration.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        toast.success('Configuration exported successfully!');
      } else {
        throw new Error(`Export failed: ${response.status}`);
      }
    } catch (err) {
      toast.error(`Export failed: ${err.message}`);
      console.error('Export error:', err);
    } finally {
      setIsExporting(false);
    }
  };

  const copyConfigToClipboard = async () => {
    if (exportData) {
      try {
        await navigator.clipboard.writeText(JSON.stringify(exportData, null, 2));
        toast.success('Configuration copied to clipboard!');
      } catch (err) {
        toast.error('Failed to copy to clipboard');
      }
    }
  };

  const formatConfigSize = (obj) => {
    const jsonString = JSON.stringify(obj);
    const sizeInBytes = new Blob([jsonString]).size;
    return (sizeInBytes / 1024).toFixed(1) + ' KB';
  };

  return (
    <div className="advanced-tab">
      {/* Export/Import Section */}
      <div className="advanced-section">
        <div className="section-header">
          <FontAwesomeIcon icon={faFileExport} />
          <h3>Configuration Export/Import</h3>
          <p>Backup and restore agent configurations</p>
        </div>

        <div className="export-import-controls">
          <div className="export-section">
            <h4>Export Configuration</h4>
            <p>Download the complete agent configuration including prompts and tools</p>
            <button
              className={`export-btn ${isExporting ? 'loading' : ''}`}
              onClick={handleExportConfiguration}
              disabled={isExporting}
            >
              {isExporting ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  Exporting...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faDownload} />
                  Export Configuration
                </>
              )}
            </button>
          </div>

          <div className="import-section">
            <h4>Import Configuration</h4>
            <p>Import agent configuration from a JSON file</p>
            <button
              className="import-btn"
              onClick={() => toast.info('Import feature coming soon')}
              disabled
            >
              <FontAwesomeIcon icon={faUpload} />
              Import Configuration (Coming Soon)
            </button>
          </div>
        </div>
      </div>

      {/* Raw Configuration Viewer */}
      {exportData && (
        <div className="advanced-section">
          <div className="section-header">
            <FontAwesomeIcon icon={faCode} />
            <h3>Raw Configuration</h3>
            <p>View and copy the raw JSON configuration</p>
          </div>

          <div className="config-stats">
            <div className="config-stat">
              <span className="stat-label">Size:</span>
              <span className="stat-value">{formatConfigSize(exportData)}</span>
            </div>
            <div className="config-stat">
              <span className="stat-label">Prompts:</span>
              <span className="stat-value">{exportData.prompts?.length || 0}</span>
            </div>
            <div className="config-stat">
              <span className="stat-label">Tools:</span>
              <span className="stat-value">{exportData.tools?.length || 0}</span>
            </div>
            <div className="config-stat">
              <span className="stat-label">Exported:</span>
              <span className="stat-value">
                {new Date(exportData.exported_at).toLocaleString()}
              </span>
            </div>
          </div>

          <div className="raw-config-controls">
            <button
              className="toggle-view-btn"
              onClick={() => setShowRawConfig(!showRawConfig)}
            >
              <FontAwesomeIcon icon={faCode} />
              {showRawConfig ? 'Hide' : 'Show'} Raw JSON
            </button>
            <button
              className="copy-config-btn"
              onClick={copyConfigToClipboard}
            >
              <FontAwesomeIcon icon={faDownload} />
              Copy to Clipboard
            </button>
          </div>

          {showRawConfig && (
            <div className="raw-config-viewer">
              <pre className="config-json">
                {JSON.stringify(exportData, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}

      {/* System Information */}
      <div className="advanced-section">
        <div className="section-header">
          <FontAwesomeIcon icon={faCode} />
          <h3>System Information</h3>
          <p>Technical details and system status</p>
        </div>

        <div className="system-info-grid">
          <div className="info-card">
            <div className="info-header">
              <FontAwesomeIcon icon={faCheckCircle} className="status-icon success" />
              <h4>Database Connection</h4>
            </div>
            <div className="info-details">
              <p>Connected to sankalvax_nexus</p>
              <small>Response time: ~50ms</small>
            </div>
          </div>

          <div className="info-card">
            <div className="info-header">
              <FontAwesomeIcon icon={faCheckCircle} className="status-icon success" />
              <h4>API Endpoints</h4>
            </div>
            <div className="info-details">
              <p>All agent endpoints operational</p>
              <small>Last checked: {new Date().toLocaleTimeString()}</small>
            </div>
          </div>

          <div className="info-card">
            <div className="info-header">
              <FontAwesomeIcon icon={faCheckCircle} className="status-icon success" />
              <h4>Configuration Status</h4>
            </div>
            <div className="info-details">
              <p>Agent properly configured</p>
              <small>{agentOverview?.prompt_count} prompts, {agentOverview?.tool_count} tools</small>
            </div>
          </div>

          <div className="info-card">
            <div className="info-header">
              <FontAwesomeIcon icon={faExclamationTriangle} className="status-icon warning" />
              <h4>Workflow File</h4>
            </div>
            <div className="info-details">
              <p>External YAML workflow detected</p>
              <small>Located in oldagentstructure/</small>
            </div>
          </div>
        </div>
      </div>

      {/* Development Tools */}
      <div className="advanced-section">
        <div className="section-header">
          <FontAwesomeIcon icon={faCode} />
          <h3>Development Tools</h3>
          <p>Tools for developers and system administrators</p>
        </div>

        <div className="dev-tools-grid">
          <button 
            className="dev-tool-btn"
            onClick={() => toast.info('Feature coming soon')}
          >
            <FontAwesomeIcon icon={faCode} />
            <div className="tool-info">
              <h4>Validate Configuration</h4>
              <p>Check agent config for errors</p>
            </div>
          </button>

          <button 
            className="dev-tool-btn"
            onClick={() => toast.info('Feature coming soon')}
          >
            <FontAwesomeIcon icon={faCheckCircle} />
            <div className="tool-info">
              <h4>Test Prompts</h4>
              <p>Test prompt configurations</p>
            </div>
          </button>

          <button 
            className="dev-tool-btn"
            onClick={() => toast.info('Feature coming soon')}
          >
            <FontAwesomeIcon icon={faFileExport} />
            <div className="tool-info">
              <h4>Generate Documentation</h4>
              <p>Auto-generate agent docs</p>
            </div>
          </button>

          <button 
            className="dev-tool-btn"
            onClick={() => toast.info('Feature coming soon')}
          >
            <FontAwesomeIcon icon={faSpinner} />
            <div className="tool-info">
              <h4>Performance Metrics</h4>
              <p>View agent performance data</p>
            </div>
          </button>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="advanced-section danger-section">
        <div className="section-header">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <h3>Danger Zone</h3>
          <p>Irreversible actions that affect the agent</p>
        </div>

        <div className="danger-actions">
          <button 
            className="danger-btn"
            onClick={() => toast.error('This action would reset all agent configurations')}
          >
            <FontAwesomeIcon icon={faExclamationTriangle} />
            Reset Agent Configuration
          </button>
          
          <small className="danger-warning">
            ⚠️ These actions cannot be undone. Make sure to export your configuration first.
          </small>
        </div>
      </div>
    </div>
  );
};

export default AdvancedTab;